<template>
  <v-row no-gutters>
    <v-col :class="['otpInput', otpConfig.flex]">
      <p class="pb-5" data-qa="otpinput-p-h42yv8">
        <span class="subtitle-2 font-weight-regular">{{ $t('otp.securityMsg') }}</span>
      </p>
      <OTP-INPUT
        ref="otpData"
        join-character=""
        :length="maxOTPLength"
        :class="isValid && isValidLength ? ['disableInput'] : ''"
        @onChange="handleOnChange($event)"
        @onComplete="handleOnComplete"
      />
      <v-row no-gutters justify="start" class="pt-4">
        <p v-if="showInvalidOTP && !isValid && isValidLength" data-qa="otpinput-p-khy26">
          <span
            v-if="!isValid && isValidLength"
            color="error"
            class="subtitle-2 font-weight-regular pl-2"
            v-text="isInvalidOTP()"
          />
        </p>
      </v-row>
      <v-row justify="center">
        <v-btn
          text
          :color="primaryColor"
          :disabled="isResendActive.mobile.timerActive || isResendActive.mobile.disabled"
          @mousedown="fetchOTP('resend')"
          @keydown.enter.prevent="fetchOTP('resend')"
          class="text-capitalize"
          v-text="resendOtpText"
          data-qa="resendOtp"
        />
        <v-btn
          :dark="!isVerifyDisabled"
          :disabled="isVerifyDisabled"
          :color="primaryColor"
          @mousedown="onVerifyOtpClick"
          @keydown.enter.prevent="onVerifyOtpClick"
          class="text-capitalize"
          data-qa="otpinput-verify-btn"
        >
          <v-progress-circular
            v-if="isLoading"
            indeterminate
            :color="primaryColor"
            data-qa="otpinput-progress-circular-3stf0p"
          />
          <span v-else data-qa="otpinput-span-3y6398">{{ $t('otp.btn.verifyOtp') }}</span>
          <v-icon
            v-if="isInvalidOTP() ? !isValid && isValidLength : isValid && isValidLength"
            right
            color="white"
            medium
            class="material-symbols-outlined"
            :data-qa="isInvalidOTP() ? 'otp-error' : 'otp-success'"
            v-text="isInvalidOTP() ? 'cancel' : 'mdi-check-circle'"
          />
        </v-btn>
      </v-row>
      <v-row class="pt-4" />
    </v-col>
  </v-row>
</template>

<script>
import '@/styles/common/otpInput.scss'
import event from '@/util/event'
import VueOtp2 from 'vue-otp-2'
import ShortUniqueId from 'short-unique-id'
import CommonService from '@/service/commonService.js'
import handleError from '@/core/_helpers/errorHandler'
const uuid = new ShortUniqueId({ length: 16 })

export default {
  name: 'OtpInput',
  props: {
    otpConfig: Object
  },
  components: {
    'OTP-INPUT': VueOtp2
  },
  data () {
    return {
      otp: null,
      otpCopy: undefined,
      defaultTestOTP: this.$appSettings.defaultConfigurations.defaultTestOTP,
      maxOTPLength: this.$appSettings.defaultConfigurations.maxOTPLength,
      defaultMessageCode: this.$appSettings.defaultConfigurations.b2cOtpMessageCode,
      otpRegex: this.$appSettings.defaultConfigurations.commonOTPInputRegex,
      isValid: false,
      isValidLength: false,
      showInvalidOTP: false,
      showInvalidOTPMsg: '',
      validateOTPResponse: false,
      otpTimeInterval: this.$appSettings.defaultConfigurations.otpResendTimeInterval,
      resendBtnDark: false,
      isRegMatched: false,
      shortuuid: '',
      isOTPForLogin: false,
      isResendActive: {
        mobile: {
          timerActive: false,
          disabled: false,
          counter: 0
        }
      },
      fromScreen: this.otpConfig.fromScreen || '',
      isLoading: false
    }
  },
  created () {
    event.$emit('OTP_INIT', true)
    this.isOTPForLogin =
      this.$route.path === '/login' &&
      this.$appSettings.defaultConfigurations.loginPageScreens.enableOTPForLogin
    event.$once('SEND_OTP', isTrigger => {
      if (isTrigger) {
        this.fetchOTP('initial')
      }
    })
    event.$on('reset_OTPField', arg => {
      if (arg) {
        this.resetOtpData()
      }
    })
    event.$on('retrieveLoginId_invalidOtp', () => {
      this.isValid = false
      this.showInvalidOTP = true
      this.showInvalidOTPMsg = this.$t('otp.invalidOtp')
      this.otp = ''
      this.$refs.otpData.otp = []
    })
    event.$on('otp_validation_failed', response => {
      this.isLoading = false
      this.validateOTPResponse = response
      this.processB2COTPValidationResponse()
    })
  },
  methods: {
    onVerifyOtpClick () {
      if (this.fromScreen === 'retrieveLoginId') {
        event.$emit('retrieve_Login_Id', {
          otp: this.otp,
          referenceNumber: this.shortuuid
        })
      } else {
        this.validateOTP(this.isResendActive.mobile.timerActive)
      }
    },
    handleOnComplete (input) {
      const content = input.values()
      this.otp = ''
      for (const value of content) {
        if (value) {
          this.otp = this.otp ? `${this.otp}${value}` : value
        }
      }
      if (this.otp && this.otp.length === this.maxOTPLength) {
        this.isValidLength = true
      }
    },
    handleOnChange (value) {
      let filterOTPArr = []
      if (value && value.value && value.value.length) {
        this.isValidRegEx(value.value)
        if (this.isRegMatched && value.value.length === 1) {
          this.$refs.otpData.otp.splice(value.index, 1, value.value)
          this.otpCopy = this.$refs.otpData.otp
        } else {
          this.$refs.otpData.otp.splice(value.index, 1)
          filterOTPArr = this.$refs.otpData.otp.filter(value => value !== null)
          if (value.index === this.maxOTPLength - 1 && value.value === 'Enter') {
            this.$refs.otpData.otp[value.index] = this.otpCopy[this.otpCopy.length - 1]
            this.validateOTP()
          }
        }
      }
      if (filterOTPArr < this.maxOTPLength) {
        this.isValid = false
        this.isValidLength = false
        this.showInvalidOTP = false
      }
    },
    isValidRegEx (string) {
      const pattern = new RegExp(this.otpRegex)
      this.isRegMatched = pattern.test(string)
    },
    handleClearInput () {
      this.otp = ''
      if (this.$refs.otpData && this.$refs.otpData.otp) {
        this.$refs.otpData.otp = []
      }
    },
    isInvalidOTP () {
      return this.showInvalidOTP ? this.showInvalidOTPMsg : ''
    },
    resetOtpData () {
      this.handleClearInput()
      this.isValid = false
      this.showInvalidOTP = false
      this.isResendActive.mobile.disabled = false
      this.isLoading = false
    },
    resendOTP () {
      event.$emit('OTP_RESENT', true)
      this.runTimer('mobile', this.otpTimeInterval)
      // this.handleClearInput()
      this.otp = ''
      this.$refs.otpData.otp = []
      this.showInvalidOTP = false
      this.isResendActive.mobile.disabled = true
      this.$store.dispatch('appStore/setAppData', {
        message: {
          content: this.$t('otp.resendMessage'),
          type: 'success',
          component: 'dob-snackbar'
        }
      })
    },
    async fetchOTP (type = 'initial') {
      this.resetOtpData()
      if (this.$appSettings.authConfigurations.enableB2C) {
        this.shortuuid = uuid.rnd()
        const userLoginId = sessionStorage.getItem('userId')
          ? this.isOTPForLogin
            ? sessionStorage.getItem('userId')
            : JSON.parse(sessionStorage.getItem('userId'))
          : JSON.parse(sessionStorage.getItem('username'))

        if (!this.isResendActive.mobile.timerActive) {
          this.isResendActive.mobile.timerActive = !(type === 'initial')
          this.handleClearInput()
          try {
            this.handleClearInput()
            // Call GenerateOTP API
            if (
              this.$store.getters['profileStore/profileData'].isDigiAccessAllowed ||
              this.isOTPForLogin
            ) {
              const generateOtpPayload = {
                messageCode: this.defaultMessageCode,
                userLoginId
              }
              await CommonService.getOTP(generateOtpPayload)
            } else {
              const partyData = this.$store.getters['profileStore/profileData'].partyDetails

              const generateB2BOtpPayload = {
                mobilePhoneNumber: partyData.phoneNumber || '',
                emailId: partyData.emailAddress || '',
                mobileCountryCode:
                  partyData.countryCode || this.$appSettings.defaultConfigurations.countryISDCode,
                referenceNumber: this.shortuuid,
                customerId: partyData.customerID,
                message: this.$t('login.preLoginOTPMessages.retrieveLoginId')
              }
              await CommonService.getB2BOTP(generateB2BOtpPayload)
            }

            if (type === 'resend') {
              this.resendOTP()
            }
          } catch (e) {
            handleError(e)
          }
        }
      } else {
        if (type === 'resend') {
          this.resendOTP()
        }
      }
    },
    processB2COTPValidationResponse () {
      if (this.validateOTPResponse && typeof this.validateOTPResponse === 'string') {
        if (this.validateOTPResponse.includes('200 OK')) {
          this.isValid = true
          this.showInvalidOTP = false
          this.isResendActive.mobile.disabled = true
        }
      } else {
        if (
          this.validateOTPResponse &&
          this.validateOTPResponse.causes &&
          ['FEUS-000039', 'FEUS-000027'].includes(this.validateOTPResponse.causes[0].code)
        ) {
          this.showInvalidOTPMsg = this.$t('otp.invalidOtp')
        } else if (
          this.validateOTPResponse &&
          this.validateOTPResponse.causes &&
          this.validateOTPResponse.causes[0].code === 'FEUS-000040'
        ) {
          this.showInvalidOTPMsg = this.$t('otp.expiredOtp')
        }
        this.isValid = false
        this.showInvalidOTP = true
      }
    },
    async validateOTP () {
      if (this.$appSettings.authConfigurations.enableB2C) {
        const userLoginId = sessionStorage.getItem('userId')
          ? this.isOTPForLogin
            ? sessionStorage.getItem('userId')
            : JSON.parse(sessionStorage.getItem('userId'))
          : JSON.parse(sessionStorage.getItem('username'))

        if (this.otp && this.isValidLength && !this.isValid) {
          try {
            if (
              this.$store.getters['profileStore/profileData'].isDigiAccessAllowed ||
              this.isOTPForLogin
            ) {
              const { meta: { OTP: { validateOTPInternally = undefined } = {} } = {} } = this.$route
              this.validateOTPResponse = false
              const validateOtpPayload = {
                otp: this.otp,
                userLoginId
              }

              if (validateOTPInternally) {
                sessionStorage.setItem('b2c_otp_paylod', JSON.stringify(validateOtpPayload))
                this.isLoading = true
                event.$emit(this.isOTPForLogin ? 'IS_LOGIN_OTP_VALID' : 'IS_OTP_VALID', true)
              } else {
                this.validateOTPResponse = await CommonService.validateOTP(validateOtpPayload)
                this.processB2COTPValidationResponse()
              }
            } else {
              this.validateOTPResponse = false
              const partyData = this.$store.getters['profileStore/profileData'].partyDetails
              const phoneNumberDetails = partyData.phoneNumber.split(' ')
              const validateB2BOtpPayload = {
                mobilePhoneNumber: phoneNumberDetails[1],
                emailId: partyData.emailAddress || '',
                mobileCountryCode: phoneNumberDetails[0],
                otp: this.otp,
                referenceNumber: this.shortuuid
              }

              this.validateOTPResponse = await CommonService.validateB2BOTP(validateB2BOtpPayload)
              if (this.validateOTPResponse.includes('200 OK')) {
                this.isValid = true
                this.showInvalidOTP = false
                this.isResendActive.mobile.disabled = true
              } else {
                this.isValid = false
                this.showInvalidOTP = true
                this.showInvalidOTPMsg = this.$t('otp.invalidOtp')
              }
            }
          } catch (e) {
            handleError(e)
          }
        }
      } else {
        if (this.otp && this.isValidLength && !this.isValid) {
          if (this.otp === this.defaultTestOTP) {
            this.isValid = true
            this.showInvalidOTP = false
            this.isResendActive.mobile.disabled = true
          } else {
            this.isValid = false
            this.showInvalidOTP = true
            this.showInvalidOTPMsg = this.$t('otp.invalidOtp')
          }
        }
      }
    },
    runTimer (type, timeInterval) {
      this.isResendActive[type].timerActive = true
      this.isResendActive[type].counter = timeInterval / 1000
      const timerInterval = setInterval(() => {
        this.isResendActive[type].counter -= 1
        if (this.isResendActive[type].counter < 1) {
          this.isResendActive[type].timerActive = false
          this.isResendActive.mobile.disabled = false
          clearInterval(timerInterval)
        }
      }, 1000)
    },
    publishOTP () {
      if (this.isValidLength) {
        this.handleOnComplete()
      }
    }
  },
  computed: {
    isVerifyDisabled () {
      return this.isValid || !this.isValidLength || this.showInvalidOTP || this.isLoading
    },
    resendOtpText () {
      return !this.isResendActive.mobile.timerActive
        ? this.$t('otp.btn.resendOtp')
        : `${this.$t('otp.resendTimerContent')} ${this.isResendActive.mobile.counter} ${this.$t('otp.unit.seconds')}`
    }
  },
  watch: {
    isValid () {
      event.$emit('IS_OTP_VALID', this.isValid)
    }
  },
  beforeDestroy () {
    event.$off('SEND_OTP', false)
  }
}
</script>
